import React, { DetailedHTMLProps, TextareaHTMLAttributes } from "react";

import classnames from "classnames";
import { FieldError } from "react-hook-form";

import { useWombatTheme } from "../../../theme";
import { ErrorIcon } from "../../../../icons";

type TextareaProps = {
  /**
   * Label
   */
  label: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Textarea id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: textarea_${id}_group
   *  - Label: textarea_${id}_label
   *  - Textarea: textarea_${id}
   *  - Help text: textarea_${id}_help_message
   *  - Error message: textarea_${id}_error_message
   *
   * It will also do the matching from the label to the textarea in respect to accessibility patterns
   */
  id: string;
  /**
   * Helper text
   * @default undefined
   */
  help?: string;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * Default number of rows of the areao
   * @default 4
   */
  rows?: number;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
} & DetailedHTMLProps<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>;

/**
 * Textarea are meant to be used in a constrained space.
 * (eg: The size of the textarea should serve as an advice of the length of the expected answer)
 * Therefore they will take up all available width of their parent container.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      help,
      id,
      srOnlyLabel = false,
      error = {},
      rows = 4,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const labelStyle = classnames(
      theme.form.textarea.label.base,
      disabled ? theme.form.textarea.label.disabled : theme.form.textarea.label.able,
      srOnlyLabel && "sr-only"
    );
    const textareaStyle = classnames(
      theme.form.textarea.base,
      theme.form.textarea.status[error?.message ? "error" : "default"]
    );

    const iconStyle = classnames(theme.form.textarea.icon.error);
    const helpStyle = classnames(
      theme.form.textarea.help[error?.message ? "error" : "default"]
    );

    return (
      <div data-test-id={`textarea_${id}_group`}>
        {label && (
          <label
            className={labelStyle}
            htmlFor={id}
            data-test-id={`textarea_${id}_label`}
          >
            {label}
          </label>
        )}
        <div className="relative mt-1 rounded-md shadow-sm">
          <textarea
            {...props}
            ref={ref}
            className={textareaStyle}
            data-test-id={`textarea_${id}`}
            disabled={disabled}
            aria-disabled={disabled ? "true" : "false"}
            rows={rows}
            id={id}
          />
          {error?.message && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ErrorIcon className={iconStyle} />
            </div>
          )}
        </div>
        {(help || error?.message) && (
          <p
            className={helpStyle}
            data-test-id={`textarea_${id}_${
              error?.message ? "error_message" : "help_message"
            }`}
          >
            {error?.message ? error?.message : help}
          </p>
        )}
      </div>
    );
  }
);

export default React.memo(Textarea);
